import type {
  FeedbackInput,
  FeedbackResult,
  PortalAssignment,
  PortalMe,
  PortalShortlistItem,
  PortalTimesheet,
  Timesheet,
  TimesheetEntry,
} from "@staffsmith/api-types";
import type { Auth } from "@staffsmith/web-auth";

// The portal's view of the StaffSmith API: the Portal operations in
// api/openapi.yaml, plus the client's approval (approveTimesheet). Types come
// from the contract, so a contract change that breaks the portal breaks the
// typecheck, not a user.

export type FieldError = { field: string; message: string };

// An RFC 9457 problem from the API, readable in the UI.
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly title: string,
    readonly detail?: string,
    readonly errors: FieldError[] = [],
    readonly requestId?: string,
  ) {
    super(detail ? `${title}: ${detail}` : title);
  }
}

export function createApi(
  baseUrl: string,
  auth: Auth,
  doFetch: typeof fetch = fetch.bind(globalThis),
) {
  async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const token = await auth.accessToken();
    if (!token) throw new ApiError(401, "Not signed in");
    // Every POST carries an Idempotency-Key, so one whose answer was lost to the
    // network is retried once safely: the API replays it rather than acting twice.
    const idempotency: Record<string, string> =
      method === "POST" ? { "idempotency-key": crypto.randomUUID() } : {};
    const send = () =>
      doFetch(`${baseUrl}${path}`, {
        method,
        headers: {
          authorization: `Bearer ${token}`,
          ...(body === undefined ? {} : { "content-type": "application/json" }),
          ...idempotency,
        },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      });
    const res = await send().catch((e: unknown) => {
      if (method !== "POST") throw e;
      return send();
    });
    const data = (await res.json().catch(() => undefined)) as Record<string, unknown> | undefined;
    if (!res.ok) {
      throw new ApiError(
        res.status,
        typeof data?.title === "string" ? data.title : `Request failed (${res.status})`,
        typeof data?.detail === "string" ? data.detail : undefined,
        Array.isArray(data?.errors) ? (data.errors as FieldError[]) : [],
        res.headers.get("x-request-id") ?? undefined,
      );
    }
    return data as T;
  }

  return {
    me: () => request<PortalMe>("GET", "/v1/portal/me"),
    shortlist: () => request<{ data: PortalShortlistItem[] }>("GET", "/v1/portal/shortlist"),
    // Recorded against the signed-in contact; "not a fit" or an offer request
    // only opens a decision for staff.
    feedback: (submissionId: string, input: FeedbackInput) =>
      request<FeedbackResult>("POST", `/v1/portal/submissions/${submissionId}/feedback`, input),
    assignments: () => request<{ data: PortalAssignment[] }>("GET", "/v1/portal/assignments"),
    // What's left before they start (Phase 9); read only.
    onboarding: () =>
      request<{
        tasks: {
          id: string;
          label: string;
          client: string;
          status: string;
          due_on: string | null;
          how: string | null;
        }[];
        credentials_expiring: { id: string; name: string; expires_on: string }[];
      }>("GET", "/v1/portal/onboarding"),
    // Their privacy requests (Phase 17); the sign-in verifies who asked.
    privacyRequests: () =>
      request<{
        data: {
          id: string;
          kind: string;
          status: string;
          received_at: string;
          due_on: string;
          closed_at: string | null;
        }[];
      }>("GET", "/v1/portal/privacy-requests"),
    askPrivacy: (kind: string, details: string) =>
      request<{ id: string; status: string; due_on: string }>(
        "POST",
        "/v1/portal/privacy-requests",
        {
          kind,
          ...(details ? { details } : {}),
        },
      ),
    // Their skill profile and suggested corrections (design v0.6, C9f).
    mySkills: () =>
      request<{
        skills: {
          skill_key: string;
          name: string;
          evidence: string | null;
          years: number | null;
        }[];
        suggestions: {
          id: string;
          change: "add" | "remove";
          name: string;
          note: string | null;
          status: string;
        }[];
      }>("GET", "/v1/portal/skills"),
    suggestSkill: (change: "add" | "remove", name: string, note: string) =>
      request<{ id: string }>("POST", "/v1/portal/skills/suggestions", {
        change,
        name,
        ...(note ? { note } : {}),
      }),
    // Their pay statements, newest first (Phase 10); read only.
    payStatements: () =>
      request<{
        data: {
          id: string;
          pay_date: string;
          week_start: string;
          client: string;
          hours: {
            regular: number;
            overtime: number;
            double_time: number;
            holiday: number;
            pto: number;
          };
          gross: number;
          taxes: number;
          deductions: number;
          net: number;
          currency: string;
        }[];
      }>("GET", "/v1/portal/pay-statements"),
    timesheets: (status?: string) =>
      request<{ data: PortalTimesheet[] }>(
        "GET",
        `/v1/portal/timesheets${status ? `?status=${encodeURIComponent(status)}` : ""}`,
      ),
    startWeek: (placementId: string, weekStart: string) =>
      request<PortalTimesheet>("POST", "/v1/portal/timesheets", {
        placement_id: placementId,
        week_start: weekStart,
        entries: [],
      }),
    saveEntries: (id: string, entries: TimesheetEntry[]) =>
      request<PortalTimesheet>("PUT", `/v1/portal/timesheets/${id}/entries`, { entries }),
    submit: (id: string) => request<PortalTimesheet>("POST", `/v1/portal/timesheets/${id}/submit`),
    // The client approver's decision; a return needs the reason.
    decide: (id: string, action: "approve" | "return", comment?: string) =>
      request<Timesheet>("POST", `/v1/timesheets/${id}/approval`, {
        action,
        ...(comment ? { comment } : {}),
      }),
  };
}

export type Api = ReturnType<typeof createApi>;
